import { store } from '@/app/store';
import config from '@/config';
import {
   resetCallkitData,
   setMakeCallDetails,
   initializeParticipants,
   updateParticipantStatus,
   setCallScreenStatus,
   setCallMode,
   addParticipantToCall,
   removeParticipantFromCall,
   setCallType,
} from '../model';
import { SDK } from '@/infrastructure/sdk';
import {
   CALL_MODES,
   CALL_TYPE,
   CALLKIT_CALLSCREENSTATUS,
   CALLKIT_CALLSTATUS,
   NEGOTIATION_TIMED_OUT,
   NOT_ABLE_TO_ANSWER,
   NOT_ABLE_TO_MUTE_AUDIO,
   NOT_ABLE_TO_MUTE_VIDEO,
   NOT_ABLE_TO_UNMUTE_AUDIO,
   NOT_ABLE_TO_UNMUTE_VIDEO,
   SOMETHING_WENT_WRONG,
   NO_ONGOINGCALL,
   CLIENT_INITIATED_DISCONNECT,
} from '../constants';
import { logger, showErrorToast, showInfoToast } from '@/shared/utils';
import { blockOfflineAction } from '@/shared/utils/network';
import { getLocalUserName, handleCallError, isAlreadyInCall, isOwnUserReconnecting } from './helper';
import { isEnabled } from '../subFeatureRegistry';
import { CallMediaStore } from './CallMediaStore';
import { i18n } from '@/localization/i18n';
import { getIsBlockedByMe } from '@/features/users';

export const makeOneToOneCallKitAudioCall = activeChatId => {
   if (blockOfflineAction() || isOwnUserReconnecting()) return;
   if (isAlreadyInCall()) return;
   if (getIsBlockedByMe(activeChatId)) {
      showErrorToast(i18n.t('calls.NOT_ALLOWED_TO_MAKECALL'));
      return;
   }
   const currentUserId = getLocalUserName();
   const participants = [
      {
         userId: currentUserId,
         callStatus: CALLKIT_CALLSTATUS.CONNECTED,
         isAudioMuted: false,
         isVideoMuted: true,
         stream: null,
      },
      {
         userId: activeChatId,
         callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
         isAudioMuted: false,
         isVideoMuted: true,
         stream: null,
      },
   ];
   store.dispatch(initializeParticipants(participants));
   SDK.makeCall({ calleeId: [activeChatId], isMicrophoneEnabled: true, isVideoCall: false }, (success, err) => {
      if (err) {
         logger.log('Err', err);
         if (err?.message === CLIENT_INITIATED_DISCONNECT) {
            showInfoToast(i18n.t('common.CALL_ENDED'));
         } else if (!handleCallError(err)) {
            showErrorToast(err?.message || SOMETHING_WENT_WRONG);
         }
         store.dispatch(resetCallkitData());
      } else {
         store.dispatch(
            setMakeCallDetails({
               calleeId: [activeChatId],
               isMicrophoneEnabled: true,
               isVideoCall: false,
               callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
               callMode: CALL_MODES.ONETOONE,
               callType: CALL_TYPE.AUDIO,
            }),
         );
      }
   });
};

export const makeOneToOneCallKitVideoCall = activeChatId => {
   if (blockOfflineAction() || isOwnUserReconnecting()) return;
   if (isAlreadyInCall()) return;
   if (getIsBlockedByMe(activeChatId)) {
      showErrorToast(i18n.t('calls.NOT_ALLOWED_TO_MAKECALL'));
      return;
   }
   const currentUserId = getLocalUserName();
   const participants = [
      {
         userId: currentUserId,
         callStatus: CALLKIT_CALLSTATUS.CONNECTED,
         isAudioMuted: false,
         isVideoMuted: false,
         stream: null,
      },
      {
         userId: activeChatId,
         callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
         isAudioMuted: false,
         isVideoMuted: false,
         stream: null,
      },
   ];
   store.dispatch(initializeParticipants(participants));
   SDK.makeCall({ calleeId: [activeChatId], isMicrophoneEnabled: true, isVideoCall: true }, (success, err) => {
      if (err) {
         logger.log('Err', err);
         if (err?.message === CLIENT_INITIATED_DISCONNECT) {
            showInfoToast(i18n.t('common.CALL_ENDED'));
         } else if (!handleCallError(err)) {
            showErrorToast(err?.message || SOMETHING_WENT_WRONG);
         }
         store.dispatch(resetCallkitData());
      } else {
         store.dispatch(
            setMakeCallDetails({
               calleeId: [activeChatId],
               isMicrophoneEnabled: true,
               isVideoCall: true,
               callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
               callMode: CALL_MODES.ONETOONE,
               callType: CALL_TYPE.VIDEO,
            }),
         );
      }
   });
};

export const makeGroupCallKitCall = (calleeId, groupId, isVideoCall, groupName = null) => {
   if (blockOfflineAction() || isOwnUserReconnecting()) return;
   if (isAlreadyInCall()) return;
   const filteredCalleeId = calleeId.filter(id => !getIsBlockedByMe(id));

   if (calleeId.length > 0 && filteredCalleeId.length === 0) {
      showErrorToast(i18n.t('calls.NOT_ALLOWED_TO_MAKECALL'));
      return;
   }

   if (filteredCalleeId.length + 1 > config.maxCallParticipants) {
      showErrorToast(i18n.t('calls.MAX_LIMIT_REACHED', { count: config.maxCallParticipants }));
      return;
   }

   const currentUserId = getLocalUserName();
   const participants = filteredCalleeId.map(id => ({
      userId: id,
      callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
      isAudioMuted: false,
      isVideoMuted: true,
      stream: null,
   }));
   participants.push({
      userId: currentUserId,
      callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
      isAudioMuted: false,
      isVideoMuted: !isVideoCall,
      stream: null,
   });
   store.dispatch(initializeParticipants(participants));
   store.dispatch(
      setMakeCallDetails({
         calleeId: filteredCalleeId,
         groupId,
         groupName,
         isMicrophoneEnabled: true,
         isVideoCall,
         callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
         callMode: CALL_MODES.ONETOMANY,
         callType: isVideoCall ? CALL_TYPE.VIDEO : CALL_TYPE.AUDIO,
      }),
   );

   SDK.makeGroupCall(
      { calleeId: filteredCalleeId, groupId, isMicrophoneEnabled: true, isVideoCall },
      (success, err) => {
         if (err) {
            logger.log(err);
            store.dispatch(resetCallkitData());
            if (err?.message === CLIENT_INITIATED_DISCONNECT) {
               showInfoToast(i18n.t('common.CALL_ENDED'));
            } else if (!handleCallError(err)) {
               showErrorToast(err?.message || i18n.t('calls.SOMETHING_WENT_WRONG', { defaultValue: 'Something went wrong' }));
            }
         } else {
            store.dispatch(
               setMakeCallDetails({
                  calleeId: filteredCalleeId,
                  groupId,
                  groupName,
                  isMicrophoneEnabled: true,
                  isVideoCall,
                  callStatus: CALLKIT_CALLSTATUS.CALLING,
                  callMode: CALL_MODES.ONETOMANY,
                  callType: isVideoCall ? CALL_TYPE.VIDEO : CALL_TYPE.AUDIO,
               }),
            );
            if (success?.participants && Array.isArray(success.participants)) {
               success.participants.forEach(participant => {
                  store.dispatch(
                     updateParticipantStatus({
                        userId: participant.identity,
                        ...participant,
                        callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
                     }),
                  );
               });
            }
         }
      },
   );
};

export const attendCallKitCall = () => {
   if (blockOfflineAction() || isOwnUserReconnecting()) return;
   SDK.connectCall((success, err) => {
      if (err) {
         logger.log(err);
         if (err.message != NEGOTIATION_TIMED_OUT || err.message != NO_ONGOINGCALL) {
            if (!handleCallError(err)) {
               showErrorToast(err?.message || NOT_ABLE_TO_ANSWER);
            }
         }
         rejectCallKitCall();
      } else {
         logger.log(success);
         store.dispatch(setCallScreenStatus(CALLKIT_CALLSCREENSTATUS.CONNECTED));
      }
   });
};

export const endCallKitCall = callback => {
   store.dispatch(resetCallkitData());
   CallMediaStore.clearStore();
   SDK.disconnectCall((success, err) => {
      if (err) {
         logger.log(err);
      } else {
         logger.log(success);
      }
      if (typeof callback === 'function') {
         callback(success, err);
      }
   });
};

export const rejectCallKitCall = () => {
   store.dispatch(resetCallkitData());
   CallMediaStore.clearStore();
   SDK.rejectCall((success, err) => {
      if (err) {
         logger.log(err);
      } else {
         logger.log(success);
      }
   });
};

export const toggleCallKitAudio = isAudioMuted => {
   if (isOwnUserReconnecting() || blockOfflineAction()) return;
   const newMuted = !isAudioMuted;
   const enable = !newMuted;
   const currentUserId = getLocalUserName();

   // Optimistic update: write intended state immediately so the next click
   // (after the 1-second cooldown) reads the correct value, not stale pre-callback state.
   store.dispatch(updateParticipantStatus({ userId: currentUserId, isAudioMuted: newMuted }));

   SDK.setMicrophoneEnable(enable, (success, err) => {
      if (err) {
         // Revert to original state if SDK rejected the toggle
         store.dispatch(updateParticipantStatus({ userId: currentUserId, isAudioMuted: isAudioMuted }));
         if (!handleCallError(err)) {
            showErrorToast(err?.message || (enable ? NOT_ABLE_TO_UNMUTE_AUDIO : NOT_ABLE_TO_MUTE_AUDIO));
         }
      }
      // On success the optimistic state is already correct — no further dispatch needed
   });
};

export const toggleCallKitVideo = isVideoMuted => {
   if (isOwnUserReconnecting() || blockOfflineAction()) return;
   const newMuted = !isVideoMuted;
   const enable = !newMuted;
   const currentUserId = getLocalUserName();

   // Optimistic update: write intended state immediately so the next click
   // (after the 1-second cooldown) reads the correct value, not stale pre-callback state.
   store.dispatch(updateParticipantStatus({ userId: currentUserId, isVideoMuted: newMuted }));

   SDK.setCameraEnable(enable, (success, err) => {
      if (err) {
         // Revert to original state if SDK rejected the toggle
         store.dispatch(updateParticipantStatus({ userId: currentUserId, isVideoMuted: isVideoMuted }));
         showErrorToast(err?.message || (enable ? NOT_ABLE_TO_UNMUTE_VIDEO : NOT_ABLE_TO_MUTE_VIDEO));
      } else {
         // On success the optimistic state is already correct.
         const { ongoingCallParticipants, callType, callMode } = store.getState().callkitData;
         if (callMode !== CALL_MODES.ONETOONE) return;

         if (!isEnabled('videoSwitchEnable')) {
            // Group-call-like behavior: turning video on directly upgrades the call to
            // video for both sides; turning all videos off never downgrades it back.
            if (enable && callType === CALL_TYPE.AUDIO) {
               store.dispatch(setCallType(CALL_TYPE.VIDEO));
            }
            return;
         }

         // Legacy behavior: downgrade to audio if both users are muted in 1-to-1 video call.
         if (callType === CALL_TYPE.VIDEO) {
            const activeParticipants = ongoingCallParticipants.filter(p => p.callStatus === CALLKIT_CALLSTATUS.CONNECTED);
            const allVideoMuted = activeParticipants.length > 0 && activeParticipants.every(p => p.isVideoMuted);
            if (allVideoMuted) {
               store.dispatch(setCallType(CALL_TYPE.AUDIO));
            }
         }
      }
   });
};

export const switchVideoCall = (status, callback) => {
   if (isOwnUserReconnecting() || blockOfflineAction()) return;
   SDK.videoCallSwitch(status, callback);
};

export const inviteUsersToCallKit = userList => {
   if (isOwnUserReconnecting() || blockOfflineAction()) return;
   const filteredUserList = userList.filter(userId => !getIsBlockedByMe(userId));

   if (userList.length > 0 && filteredUserList.length === 0) {
      showErrorToast(i18n.t('calls.NOT_ALLOWED_TO_MAKECALL'));
      return;
   }

   const { ongoingCallParticipants } = store.getState().callkitData;
   const currentParticipantCount = ongoingCallParticipants.filter(p => !p.userId.endsWith('-screen')).length;
   if (currentParticipantCount + filteredUserList.length > config.maxCallParticipants) {
      showErrorToast(i18n.t('calls.MAX_LIMIT_REACHED', { count: config.maxCallParticipants }));
      return;
   }
   SDK.inviteUsersToOngoingCall({ inviteUserList: filteredUserList }, (success, err) => {
      if (err) {
         showErrorToast(err?.message || SOMETHING_WENT_WRONG);
         logger.log(err);
      } else {
         logger.log('Invite success', success);
         if (success?.participants?.length > 0) {
            store.dispatch(setCallMode(CALL_MODES.ONETOMANY));
            success.participants.forEach(user => {
               store.dispatch(
                  addParticipantToCall({
                     userId: user.identity,
                     ...user,
                     callStatus: CALLKIT_CALLSTATUS.TRY_TO_CONNECT,
                  }),
               );
            });
         }
      }
   });
};

export const subscribeCallEvent = (serverLink, callback) => {
   SDK.subscribeCallEvent({ callLink: serverLink }, (success, err) => {
      if (err) {
         logger.log('err', err);
      }
      if (success) {
         logger.log('success', success);
      }
      callback(success, err);
   });
};

export const unSubscribeCallEvent = callback => {
   SDK.unSubscribeCallEvent((success, err) => {
      if (err) {
         logger.log('err', err);
      }
      if (success) {
         logger.log('success', success);
      }
      callback(success, err);
   });
};

export const joinCallEvent = (audioMute, videoMute, callback) => {
   if (blockOfflineAction()) return;
   SDK.JoinLink({ isMicrophoneEnable: !audioMute, isCameraEnable: !videoMute }, (success, err) => {
      if (err) {
         logger.log('err', err);
         handleCallError(err);
      }
      if (success) {
         logger.log('success', success);
      }
      callback(success, err);
   });
};

export const getCallLogs = ({ lastSyncTime, direction, size }, callback) => {
   SDK.getCallLog({ lastSyncTime, direction, size }, (success, err) => {
      if (err) {
         console.log('err', err);
      }
      if (success) {
         console.log('success', success);
      }
      callback(success, err);
   });
};

export const deleteCallLogs = ({ callIds, clearAll }, callback) => {
   SDK.deleteCallLog({ callIds, clearAll }, (success, err) => {
      if (err) {
         console.log('err', err);
         showErrorToast(SOMETHING_WENT_WRONG);
      }
      if (success) {
         console.log('success', success);
      }
      callback(success, err);
   });
};

export const seenCallLogs = ({ callIds, markAll }, callback) => {
   SDK.callSeen({ callIds, markAll }, (success, err) => {
      if (err) {
         console.log('err', err);
      }
      if (success) {
         console.log('success', success);
      }
      callback(success, err);
   });
};

export const scheduleMeet = ({ toJid, msgId, title, link, scheduledDateTime }) => {
   if (blockOfflineAction()) return;
   const response = SDK.sendMeetMessage({
      toJid,
      msgId,
      title,
      link,
      scheduledDateTime,
   });
   return response;
};

export const startScreenCapture = callback => {
   if (blockOfflineAction()) return;
   SDK.startScreenCapture((success, err) => {
      if (err) {
         logger.log('startScreenCapture error', err);
      }
      if (success) {
         logger.log('startScreenCapture success', success);
      }
      callback(success, err);
   });
};

export const stopScreenCapture = callback => {
   if (blockOfflineAction()) return;
   SDK.stopScreenCapture((success, err) => {
      if (err) {
         logger.log('stopScreenCapture error', err);
         showErrorToast(err?.message || SOMETHING_WENT_WRONG);
      }
      if (success) {
         console.log('success', success);
         const currentUserId = getLocalUserName();
         const screenUserId = currentUserId + '-screen';
         store.dispatch(removeParticipantFromCall(screenUserId));
         CallMediaStore.removeTrack(screenUserId, 'video');
         store.dispatch(updateParticipantStatus({ userId: currentUserId, trackUpdated: Date.now() }));
      }
      if (typeof callback === 'function') {
         callback(success, err);
      }
   });
};
